import React from 'react';
import Input from '../../Components/Input';
import ColorSelector from './ColorSelector';
import { BuilderActionType, BuilderContext } from './state';
import { getStyleProperties } from '../../utils/builderUtils';
import Select from '../../Components/Select';
import NumberInput from './NumberInput';
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    Type,
} from '../../Components/Icon';
import { BuilderPanel, BuilderPillButton } from './BuilderPanel';

type Props = {
    selector: string;
    hasAlignment?: boolean;
};

function TypographySelector({ selector, hasAlignment = true }: Props) {
    const styleObj = getStyleProperties(selector, [
        'font-family',
        'font-size',
        'font-weight',
        'font-style',
        'text-align',
        'color',
    ]);
    const [typo, setTypo] = React.useState({ ...styleObj });

    const { dispatch } = React.useContext(BuilderContext);

    const handleChange = (name: string, value: string) => {
        setTypo({ ...typo, [name]: value });
        dispatch({
            type: BuilderActionType.UPDATE_CSS,
            payload: { selector, properties: { ...typo, [name]: value } },
        });
    };

    return (
        <BuilderPanel
            title="Typography"
            icon={<Type className="h-4 w-4" />}
            className="mt-5"
            bodyClassName="space-y-4"
        >
            {hasAlignment && (
                <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Align
                    </p>
                    <BuilderPillButton
                        active={typo['text-align'] === 'left'}
                        onClick={() => handleChange('text-align', 'left')}
                        icon={<AlignLeft className="h-4 w-4" />}
                        aria-label="Align left"
                        title="Align left"
                        className="h-10 w-10 px-0"
                    />
                    <BuilderPillButton
                        active={typo['text-align'] === 'center'}
                        onClick={() => handleChange('text-align', 'center')}
                        icon={<AlignCenter className="h-4 w-4" />}
                        aria-label="Align center"
                        title="Align center"
                        className="h-10 w-10 px-0"
                    />
                    <BuilderPillButton
                        active={typo['text-align'] === 'right'}
                        onClick={() => handleChange('text-align', 'right')}
                        icon={<AlignRight className="h-4 w-4" />}
                        aria-label="Align right"
                        title="Align right"
                        className="h-10 w-10 px-0"
                    />
                </div>
            )}
            <div className="grid gap-3 md:grid-cols-3">
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Size
                    </p>
                    <NumberInput
                        options={['px', 'em', 'rem']}
                        defaultOption="px"
                        value={typo['font-size']}
                        onChange={handleChange}
                        name="font-size"
                    />
                </div>
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Weight
                    </p>
                    <Select
                        value={typo['font-weight']}
                        onChange={(e) =>
                            handleChange('font-weight', e.target.value)
                        }
                    >
                        <option value="">Select weight</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={300}>300</option>
                        <option value={400}>400</option>
                        <option value={500}>500</option>
                        <option value={600}>600</option>
                        <option value={700}>700</option>
                        <option value={800}>800</option>
                        <option value={900}>900</option>
                    </Select>
                </div>
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Style
                    </p>
                    <Select
                        value={typo['font-style']}
                        onChange={(e) =>
                            handleChange('font-style', e.target.value)
                        }
                    >
                        <option value="">Select style</option>
                        <option value="italic">italic</option>
                        <option value="normal">normal</option>
                    </Select>
                </div>
                <div className="md:col-span-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Font family
                    </p>
                    <Input
                        value={typo['font-family']}
                        onChange={(e) =>
                            handleChange('font-family', e.target.value)
                        }
                    />
                </div>
                <div className="flex items-end justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Color
                    </p>
                    <ColorSelector
                        color={typo.color}
                        onChange={(value) => handleChange('color', value)}
                    />
                </div>
            </div>
        </BuilderPanel>
    );
}

export default TypographySelector;
